import React, { useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { resolveAssetUrl } from "../utils/assets";

const VideoCard = ({ src, poster, title, subtitle, testId }) => {
  const { tx } = useLanguage();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="testimonial-card relative rounded-3xl overflow-hidden bg-[#0a0f1a] group" data-testid={testId}>
      <div className="relative aspect-[9/16] sm:aspect-video w-full overflow-hidden">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          className="w-full h-full object-cover"
          playsInline
          preload="metadata"
          controls={playing}
          onPause={() => setPlaying(false)}
          onPlay={() => setPlaying(true)}
          onEnded={() => setPlaying(false)}
        />
        {!playing && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label={tx("Luaj video")}
            className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/70 via-black/30 to-black/20 transition-all duration-300 group-hover:from-black/60"
            data-testid={`${testId}-play`}
          >
            <span
              className="flex items-center justify-center rounded-full transition-transform duration-300 group-hover:scale-110"
              style={{
                width: 84,
                height: 84,
                background: "linear-gradient(135deg, #3CD4E1, #1CA3B0)",
                boxShadow: "0 12px 40px rgba(60,212,225,0.45)",
              }}
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="#003F72">
                <path d="M8 5v14l11-7z" />
              </svg>
            </span>
          </button>
        )}
      </div>
      <div className="absolute left-0 right-0 bottom-0 px-6 py-5 pointer-events-none" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0))" }}>
        <p className="text-[#3CD4E1] text-[11px] font-bold uppercase tracking-[0.18em] mb-1.5">{tx("Deshmi")}</p>
        <h4 className="text-white font-bold text-base sm:text-lg leading-snug" style={{ fontFamily: "'Noto Sans', sans-serif" }}>{title}</h4>
        {subtitle && <p className="text-white/70 text-xs sm:text-sm mt-1">{subtitle}</p>}
      </div>
    </div>
  );
};

const TestimonialsSection = ({
  // tx-catalog-start
  variant = "light",
  eyebrow = "Testimoniale",
  heading = "Deshmi nga eventet e kaluara",
  description = "Degjoni pjesemarresit tane qe ndane pervojen e tyre nga konferencat dhe workshop-et e organizuara nga Albanian Aligners Academy.",
  // tx-catalog-end
  testId = "testimonials-section",
}) => {
  const { tx } = useLanguage();
  const isDark = variant === "dark";
  const videos = [
    {
      src: resolveAssetUrl("/api/files/local/testimonial-chris-chang.mp4"),
      poster: resolveAssetUrl("/api/files/local/testimonial-chris-chang-poster.jpg"),
      title: tx("Deshmi nga pjesemarresit"),
      subtitle: tx("Eventi i Dr. Chris Chang"),
      testId: "testimonial-chris-chang",
    },
    {
      src: resolveAssetUrl("/api/files/local/testimonial-diego-peydro.mp4"),
      poster: resolveAssetUrl("/api/files/local/testimonial-diego-peydro-poster.jpg"),
      title: tx("Deshmi nga pjesemarresit"),
      subtitle: tx("Eventi i Dr. Diego Peydro"),
      testId: "testimonial-diego-peydro",
    },
  ];

  return (
    <section
      className="py-20 md:py-32 px-6 md:px-12 relative overflow-hidden"
      style={{ background: isDark ? "#0a0f1a" : "#f8fafb" }}
      data-testid={testId}
    >
      {isDark && (
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-[8%] w-72 h-72 rounded-full opacity-[0.06]" style={{ background: "radial-gradient(circle, #3CD4E1, transparent 70%)" }} />
          <div className="absolute bottom-20 right-[10%] w-64 h-64 rounded-full opacity-[0.05]" style={{ background: "radial-gradient(circle, #1CA3B0, transparent 70%)" }} />
        </div>
      )}
      <div className="max-w-[1350px] mx-auto relative z-10">
        <div className="mb-14">
          <p className={`text-[11px] font-bold uppercase tracking-[0.2em] mb-3 ${isDark ? "text-[#3CD4E1]" : "text-[#1CA3B0]"}`}>{tx(eyebrow)}</p>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-['Inter_Tight'] mb-5 ${isDark ? "text-white" : "text-[#0A0D10]"}`}>
            {tx(heading)}
          </h2>
          <p className={`leading-relaxed text-base max-w-3xl ${isDark ? "text-white/60" : "text-[#4A5568]"}`}>
            {tx(description)}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {videos.map((v) => (
            <VideoCard key={v.testId} {...v} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
